// 🛡️ Secure Password Hash Service - Sistema de Hash de Senhas Seguro
import CryptoJS from 'crypto-js';

class SecurePasswordHash {
  constructor() {
    // Pepper fixo - deve ser único por aplicação (em produção, viria do servidor)
    this.pepper = process.env.REACT_APP_PASSWORD_PEPPER || 'SGV_PEPPER_2024_V2';
    this.saltLength = 16;
    this.iterations = 10000;
  }

  /**
   * Gera hash seguro de senha usando PBKDF2
   * @param {string} password - Senha em texto plano
   * @param {string} username - Nome de usuário (usado como salt adicional)
   * @returns {object} Objeto com hash, salt e versão
   */
  static hashPassword(password, username) {
    if (!password || typeof password !== 'string') {
      throw new Error('Senha inválida');
    }

    // Gerar salt único
    const salt = CryptoJS.lib.WordArray.random(this.saltLength);

    // Combinar pepper + password + username + salt
    const combinedData = this.pepper + password + username + salt.toString();

    // Usar PBKDF2 para hash forte
    const hash = CryptoJS.PBKDF2(combinedData, salt, {
      keySize: 256 / 32, // 256 bits
      iterations: this.iterations
    });

    return {
      hash: hash.toString(),
      salt: salt.toString(),
      version: '2.0',
      algorithm: 'PBKDF2-SHA256',
      iterations: this.iterations,
      createdAt: new Date().toISOString()
    };
  }

  /**
   * Verifica se a senha está correta
   * @param {string} inputPassword - Senha fornecida pelo usuário
   * @param {object} storedHashData - Dados do hash armazenado
   * @param {string} username - Nome de usuário
   * @returns {boolean} True se a senha está correta
   */
  static verifyPassword(inputPassword, storedHashData, username) {
    if (!storedHashData || !storedHashData.hash || !storedHashData.salt) {
      return false;
    }

    // Reconstruir o hash com os mesmos parâmetros
    const salt = CryptoJS.enc.Hex.parse(storedHashData.salt);
    const combinedData = this.pepper + inputPassword + username + storedHashData.salt;

    const computedHash = CryptoJS.PBKDF2(combinedData, salt, {
      keySize: 256 / 32,
      iterations: this.iterations
    });

    // Comparação segura (timing-safe)
    return this.timingSafeEqual(computedHash.toString(), storedHashData.hash);
  }

  /**
   * Comparação segura contra timing attacks
   */
  static timingSafeEqual(a, b) {
    if (a.length !== b.length) {
      return false;
    }

    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }

    return result === 0;
  }

  /**
   * Gera hash para novos usuários (armazena apenas o hash, não a senha)
   */
  static hashForStorage(password, username) {
    const hashData = this.hashPassword(password, username);

    // Retornar apenas o hash e salt (não armazenar a versão/pepper no localStorage)
    return {
      hash: hashData.hash,
      salt: hashData.salt
    };
  }

  /**
   * Verifica se o hash precisa ser atualizado (algoritmo antigo)
   */
  static needsHashUpdate(storedHashData) {
    if (!storedHashData || !storedHashData.algorithm) {
      return true; // Hash muito antigo, precisa atualizar
    }

    return false; // Hash atualizado
  }
}

export default SecurePasswordHash;
